import { Trans } from '@lingui/react/macro';
import { Target, Lock, Circle } from 'lucide-react';

import { Card, CardContent } from '~/components/ui/card';

interface AnalysisData {
  fillerWords?: string[];
  speechPace?: string | null;
  clarity?: string | null;
  videoDurationSeconds?: number;
  personalizedTips?: string[];
  sentiment?: string | null;
}

interface NextAttemptFocusProps {
  analysisData: AnalysisData | null;
  isLocked?: boolean;
  onUnlock?: () => void;
}

interface FocusItem {
  text: string;
  priority: 'high' | 'medium' | 'low';
}

function generateFocusItems(data: AnalysisData): FocusItem[] {
  const items: FocusItem[] = [];

  // Filler words
  const fillerCount = data.fillerWords?.length || 0;
  if (fillerCount > 5) {
    items.push({
      text: 'Eliminate filler words — pause instead of saying "um" or "so"',
      priority: 'high',
    });
  } else if (fillerCount > 2) {
    items.push({
      text: 'Reduce filler words in your opening sentence',
      priority: 'medium',
    });
  }

  // Speech pace
  if (data.speechPace === 'Fast') {
    items.push({
      text: 'Slow down your speaking pace — take a breath between sentences',
      priority: 'high',
    });
  } else if (data.speechPace === 'Slow') {
    items.push({
      text: 'Pick up your pace slightly to maintain energy',
      priority: 'medium',
    });
  }

  // Duration
  const duration = data.videoDurationSeconds || 0;
  if (duration > 120) {
    items.push({
      text: 'Keep your answer under 90 seconds',
      priority: 'high',
    });
  } else if (duration < 30 && duration > 0) {
    items.push({
      text: 'Expand your answer with more specific details',
      priority: 'medium',
    });
  }

  // Clarity
  if (data.clarity === 'Muffled' || data.clarity === 'Unclear') {
    items.push({
      text: 'Speak more clearly — enunciate each word',
      priority: 'high',
    });
  }

  // Sentiment
  if (data.sentiment === 'Negative') {
    items.push({
      text: 'Add more enthusiasm to your delivery',
      priority: 'medium',
    });
  } else if (data.sentiment === 'Neutral') {
    items.push({
      text: 'Show more energy and confidence in your tone',
      priority: 'low',
    });
  }

  // Add from personalized tips if we have room
  if (items.length < 5 && data.personalizedTips && data.personalizedTips.length > 0) {
    for (const tip of data.personalizedTips) {
      if (items.length >= 5) break;
      // Skip if we already have a similar item
      const tipLower = tip.toLowerCase();
      const alreadyHas = items.some(
        (item) =>
          item.text.toLowerCase().includes('filler') && tipLower.includes('filler') ||
          item.text.toLowerCase().includes('pace') && tipLower.includes('pace') ||
          item.text.toLowerCase().includes('clarity') && tipLower.includes('clar')
      );
      if (!alreadyHas) {
        items.push({
          text: tip,
          priority: 'low',
        });
      }
    }
  }

  // Always have at least one actionable item
  if (items.length === 0) {
    items.push({
      text: 'Practice your answer one more time with a timer',
      priority: 'low',
    });
  }

  return items.slice(0, 5);
}

function getPriorityColor(priority: 'high' | 'medium' | 'low'): string {
  switch (priority) {
    case 'high':
      return 'text-red-500';
    case 'medium':
      return 'text-brand-gold';
    case 'low':
      return 'text-brand-success';
    default:
      return 'text-gray-400';
  }
}

export function NextAttemptFocus({
  analysisData,
  isLocked = false,
  onUnlock,
}: NextAttemptFocusProps) {
  if (!analysisData) return null;

  const focusItems = generateFocusItems(analysisData);

  if (isLocked) {
    return (
      <Card className="border-2 border-brand-success/20 bg-gradient-to-br from-brand-success/5 to-white overflow-hidden">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-success/10 flex items-center justify-center">
              <Target className="w-5 h-5 text-brand-success/50" />
            </div>
            <h3 className="text-sm font-bold text-brand-dark-blue flex items-center gap-2">
              <Trans>What to Focus on Next</Trans>
              <Lock className="w-3.5 h-3.5 text-brand-gold" />
            </h3>
          </div>

          <div className="relative">
            <ul className="space-y-2.5 blur-sm select-none pointer-events-none">
              <li className="flex items-start gap-2.5">
                <Circle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-500" fill="currentColor" />
                <span className="text-sm text-brand-dark-blue/80">Reduce filler words in your opening sentence</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Circle className="w-4 h-4 mt-0.5 flex-shrink-0 text-brand-gold" fill="currentColor" />
                <span className="text-sm text-brand-dark-blue/80">Keep your answer under 90 seconds</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Circle className="w-4 h-4 mt-0.5 flex-shrink-0 text-brand-success" fill="currentColor" />
                <span className="text-sm text-brand-dark-blue/80">Add more structure with STAR method</span>
              </li>
            </ul>

            <div className="absolute inset-0 flex items-center justify-center">
              {onUnlock && (
                <button
                  onClick={onUnlock}
                  className="text-xs font-semibold text-brand-gold hover:text-brand-gold/80 transition-colors flex items-center gap-1"
                >
                  <Lock className="w-3 h-3" />
                  <Trans>Unlock to view</Trans>
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-brand-success/20 bg-gradient-to-br from-brand-success/5 to-white">
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-success/10 flex items-center justify-center">
            <Target className="w-5 h-5 text-brand-success" />
          </div>
          <h3 className="text-sm font-bold text-brand-dark-blue">
            <Trans>What to Focus on Next</Trans>
          </h3>
        </div>

        <ul className="space-y-2.5">
          {focusItems.map((item, index) => (
            <li key={index} className="flex items-start gap-2.5">
              <Circle
                className={`w-4 h-4 mt-0.5 flex-shrink-0 ${getPriorityColor(item.priority)}`}
                fill="currentColor"
              />
              <span className="text-sm text-brand-dark-blue/80 leading-relaxed">
                {item.text}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-4 text-xs text-brand-dark-blue/50">
            <div className="flex items-center gap-1">
              <Circle className="w-2.5 h-2.5 text-red-500" fill="currentColor" />
              <span><Trans>High priority</Trans></span>
            </div>
            <div className="flex items-center gap-1">
              <Circle className="w-2.5 h-2.5 text-brand-gold" fill="currentColor" />
              <span><Trans>Medium</Trans></span>
            </div>
            <div className="flex items-center gap-1">
              <Circle className="w-2.5 h-2.5 text-brand-success" fill="currentColor" />
              <span><Trans>Low</Trans></span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
